import type React from 'react';
import type { CSSProperties, Ref } from 'react';
import { twMerge } from 'tailwind-merge';

import IconButton from 'components/partials/button/icon-button';
import ActionsDropdown from 'components/partials/actions-dropdown/actions-dropdown';
import type { ActionsDropdownOption } from 'components/partials/actions-dropdown/actions-dropdown';
import { ChartFrame } from 'components/partials/charts/shared/chart-frame';

import { useMediaQuery } from 'hooks/use-media-query';
import type { ChartExportFormat } from 'hooks/use-chart-export';

import { trackChartExported } from '../../../analytics/chart-analytics';

import { breakpoints } from 'src/design-tokens/primitives';

import {
  CHART_PART_FRAME_MAX_WIDTH_PX,
  CHART_PART_FRAME_OFFSET_PX,
} from './chart-layout';

import Icons from 'assets/icons';

export type ChartViewMode = 'vertical' | 'horizontal' | 'table';

interface ChartPartFrameProps {
  title?: string;
  subtitle?: string;
  viewMode: ChartViewMode;
  onViewModeChange: (viewMode: ChartViewMode) => void;
  /** Width (px) the plot would prefer. Drives card lower bound. */
  preferredContentWidthPx?: number;
  forceMaxWidth?: boolean;
  chartAreaRef?: Ref<HTMLDivElement>;
  onExport?: (format: ChartExportFormat) => Promise<boolean>;
  isExporting?: boolean;
  children: React.ReactNode;
}

const buttonClass = '!transition-none';
const activeButtonClass =
  'bg-navy-800 text-white hover:!bg-navy-900 hover:!text-white';
const LG_SCREEN_QUERY = `(min-width: ${breakpoints.lg}px)`;
const expandedFrameWidth = `min(calc(100% + ${
  CHART_PART_FRAME_OFFSET_PX * 2
}px), ${CHART_PART_FRAME_MAX_WIDTH_PX}px)`;

const EXPORT_OPTIONS: ReadonlyArray<{
  format: ChartExportFormat;
  label: string;
  icon: React.ReactNode;
}> = [
  { format: 'png', label: 'Download PNG', icon: <Icons.Camera /> },
  { format: 'csv', label: 'Download CSV', icon: <Icons.Columns /> },
];

const ChartPartFrame = ({
  title,
  subtitle,
  viewMode,
  onViewModeChange,
  preferredContentWidthPx,
  forceMaxWidth = false,
  chartAreaRef,
  onExport,
  isExporting = false,
  children,
}: ChartPartFrameProps): React.JSX.Element => {
  const isLgScreen = useMediaQuery(LG_SCREEN_QUERY);
  const exportMenuOptions: ActionsDropdownOption[] = onExport
    ? EXPORT_OPTIONS.map(({ format, label, icon }) => ({
        label,
        startIcon: icon,
        isDisabled: isExporting,
        onClick: async () => {
          const isSuccessful = await onExport(format);

          trackChartExported({
            format,
            viewMode,
            chartTitle: title,
            status: isSuccessful ? 'successful' : 'errored',
          });
        },
      }))
    : [];
  const forcedFrameWidth =
    isLgScreen && forceMaxWidth ? expandedFrameWidth : undefined;

  const frameStyle: CSSProperties | undefined =
    preferredContentWidthPx || isLgScreen
      ? {
          ...(preferredContentWidthPx && {
            minWidth: isLgScreen
              ? `min(${expandedFrameWidth}, ${preferredContentWidthPx}px)`
              : `min(100%, ${preferredContentWidthPx}px)`,
          }),
          ...(isLgScreen && {
            maxWidth: expandedFrameWidth,
            marginLeft: `-${CHART_PART_FRAME_OFFSET_PX}px`,
          }),
          ...(isLgScreen &&
            forceMaxWidth && {
              width: forcedFrameWidth,
            }),
        }
      : undefined;

  const getViewModeButtonClass = (mode: ChartViewMode): string =>
    twMerge(buttonClass, mode === viewMode && activeButtonClass);

  return (
    <ChartFrame
      chartAreaRef={chartAreaRef}
      title={title}
      subtitle={subtitle}
      className="w-fit max-w-full min-w-0 my-2"
      style={frameStyle}
      settingsBar={
        <div
          data-export-ignore="true"
          role="group"
          aria-label="Chart settings"
          className="flex w-full items-center justify-between gap-3"
        >
          <div
            role="group"
            aria-label="Chart display mode"
            className="inline-flex items-center gap-0.5"
          >
            <IconButton
              variant="neutral-text"
              size="sm"
              icon={<Icons.BarChartAscending />}
              aria-label="Vertical bar chart view"
              aria-pressed={viewMode === 'vertical'}
              onClick={() => onViewModeChange('vertical')}
              className={getViewModeButtonClass('vertical')}
            />
            <IconButton
              variant="neutral-text"
              size="sm"
              icon={<Icons.BarChartHorizontal />}
              aria-label="Horizontal bar chart view"
              aria-pressed={viewMode === 'horizontal'}
              onClick={() => onViewModeChange('horizontal')}
              className={getViewModeButtonClass('horizontal')}
            />
            <IconButton
              variant="neutral-text"
              size="sm"
              icon={<Icons.Table />}
              aria-label="Table view"
              aria-pressed={viewMode === 'table'}
              onClick={() => onViewModeChange('table')}
              className={getViewModeButtonClass('table')}
            />
          </div>
          {!!exportMenuOptions.length && (
            <ActionsDropdown
              options={exportMenuOptions}
              menuPosition="right"
              variant="neutral-text"
              loading={isExporting}
              isDisabled={isExporting}
              startIcon={<Icons.Download customClass="!size-3.5" />}
              ariaLabel="Download chart"
              customClass="h-[34px] w-[34px] p-0"
            />
          )}
        </div>
      }
    >
      <div className="min-w-0 w-full">{children}</div>
    </ChartFrame>
  );
};

export default ChartPartFrame;
